import {
  Button,
  CloseButton,
  Flex,
  Grid,
  Heading,
  IconAddLine,
  IconButton,
  IconTrashLine,
  Modal,
  Spinner,
  TextInput,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useState } from "react";
import { GoalsService } from "../../../services/CyclesService";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";

export const CycleGoalEditModal = ({ goal, open, setOpen, setGoals }) => {
  const showNotification = useNotification();
  const [goalStatement, setGoalStatement] = useState(goal?.description || "");
  const [objectives, setObjectives] = useState(
    goal?.objectives || [{ description: "" }]
  );
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setGoalStatement(goal?.description || "");
    setObjectives(goal?.objectives || [{ description: "" }]);
  };

  function filterEmptyObjectives(objectives) {
    return objectives.filter((obj) => obj.description.trim() !== "");
  }

  function handleAddObjective() {
    setObjectives([...objectives, { description: "" }]);
  }

  function handleRemoveObjective(index) {
    setObjectives((prevObjectives) =>
      prevObjectives.filter((_, i) => i !== index)
    );
  }

  async function handleCreateGoal() {
    try {
      const goalObj = {
        description: goalStatement,
        objectives: filterEmptyObjectives(objectives),
      };
      const newGoal = await GoalsService.createGoal(goalObj);
      setGoals((prevGoals) => [newGoal, ...prevGoals]);
      setOpen(false);
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("cyclesAndGoals.cycleGoal.goal"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("cyclesAndGoals.cycleGoal.goal"),
        }),
        ERROR
      );
    }
  }

  async function handleUpdateGoal() {
    try {
      const goalObj = {
        id: goal.id,
        description: goalStatement,
        objectives: filterEmptyObjectives(objectives),
      };
      const updatedGoal = await GoalsService.updateGoal(goalObj);
      setGoals((prevGoals) =>
        prevGoals.map((g) => (g.id === goal.id ? updatedGoal : g))
      );
      setOpen(false);
      showNotification(
        t("notifications.general.updateSuccess", {
          obj: t("cyclesAndGoals.cycleGoal.goal"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.updateError", {
          obj: t("cyclesAndGoals.cycleGoal.goal"),
        }),
        ERROR
      );
    }
  }

  async function handleSubmit() {
    setSubmitting(true);
    if (goal?.id) {
      await handleUpdateGoal();
    } else {
      await handleCreateGoal();
    }
    setSubmitting(false);
  }

  return (
    <Modal
      open={open}
      onExited={handleClose}
      size="large"
      label={t("cyclesAndGoals.cycleGoal.configure")}
    >
      <Modal.Header>
        <Grid hAlign="space-between">
          <Grid.Row>
            <Grid.Col>
              <Heading level="h2" as="h1">
                {t("cyclesAndGoals.cycleGoal.newCycleGoal")}
              </Heading>
            </Grid.Col>
            <Grid.Col width="auto">
              <CloseButton
                size="small"
                screenReaderLabel={t("global.close")}
                onClick={() => setOpen(false)}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
      </Modal.Header>
      <Modal.Body>
        <Flex wrap="wrap" gap="small">
          <Flex.Item width="100%" textAlign="end">
            <Button
              id="add-objective-button"
              color="primary"
              onClick={handleAddObjective}
              renderIcon={IconAddLine}
            >
              {t("cyclesAndGoals.cycleGoal.addGoalObjectiveButton")}
            </Button>
          </Flex.Item>
          <Flex.Item width="100%">
            <TextInput
              id="cycle-goal-name"
              value={goalStatement}
              renderLabel={t("cyclesAndGoals.cycleGoal.cycleGoalDescription")}
              onChange={(e) => {
                setGoalStatement(e.target.value);
              }}
            />
          </Flex.Item>
          {objectives.map((objective, index) => (
            <Flex.Item width="100%" key={index}>
              <Flex gap="small">
                <Flex.Item shouldGrow shouldShrink>
                  <TextInput
                    value={objective.description}
                    id={`objective-name-${index}`}
                    renderLabel={t(
                      "cyclesAndGoals.cycleGoal.cycleObjectiveDescription"
                    )}
                    onChange={(e) => {
                      setObjectives((prevObjectives) => {
                        const newObjectives = [...prevObjectives];
                        newObjectives[index].description = e.target.value;
                        return newObjectives;
                      });
                    }}
                  />
                </Flex.Item>
                {index !== 0 && (
                  <Flex.Item align="end">
                    <IconButton
                      renderIcon={IconTrashLine}
                      screenReaderLabel={t(
                        "cyclesAndGoals.cycleGoal.deleteObjective"
                      )}
                      onClick={() => {
                        handleRemoveObjective(index);
                      }}
                    />
                  </Flex.Item>
                )}
              </Flex>
            </Flex.Item>
          ))}
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <Button margin="0 small 0 0" onClick={() => setOpen(false)}>
          {t("global.cancel")}
        </Button>
        <Button
          color="primary"
          type="submit"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
          ) : (
            t("global.submit")
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
